import axios from './axios';
import type {
  WorkoutInput,
  CreateWorkoutRecordResponse,
} from '@shared/types/workout.types';

/**
 * 운동 기록 생성
 */
export async function createWorkoutRecord(data: WorkoutInput): Promise<CreateWorkoutRecordResponse> {
  const response = await axios.post<CreateWorkoutRecordResponse>('/workouts', data);
  return response.data;
}

/**
 * 운동 기록 조회
 */
export interface GetWorkoutRecordsParams {
  bodyPartId?: number;
  exerciseId?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface WorkoutRecordStats {
  totalWorkouts: number;
  totalCalories: number;
  totalVolume: number;
  recentPRs: Array<{
    bodyPartId: number;
    bodyPartName: string;
    exerciseName: string;
    weight: number;
    reps: number;
    date: string;
  }>;
}

export interface GetWorkoutRecordsResponse {
  workouts: Array<{
    id: string;
    exerciseId: number;
    exerciseName: string;
    bodyPartId: number;
    bodyPartName: string;
    sets: number;
    reps: number;
    weight: number;
    calculated1RM?: number;
    rmPercentage?: number;
    grade?: string;
    expGained?: number;
    caloriesBurned?: number;
    videoUrl?: string;
    notes?: string;
    verified: boolean;
    workoutDate: string;
    createdAt: string;
  }>;
  total: number;
  stats: WorkoutRecordStats;
}

export async function getWorkoutRecords(
  params?: GetWorkoutRecordsParams
): Promise<GetWorkoutRecordsResponse> {
  const response = await axios.get<GetWorkoutRecordsResponse>('/workouts', { params });
  return response.data;
}

/**
 * 1RM 계산 (클라이언트 사이드 미리보기용)
 */
export function calculate1RMPreview(weight: number, reps: number): number {
  if (reps === 1) {
    return weight;
  }
  const oneRM = weight * (1 + reps / 30);
  return Math.round(oneRM * 10) / 10;
}

/**
 * 경험치 예상 계산 (간단 버전)
 * official1RM이 있으면 상대적 강도 배율 적용
 */
export function estimateExp(
  sets: number,
  reps: number,
  weight: number,
  difficulty: number = 5,
  official1RM?: number | null
): number {
  const baseExp = sets * reps * weight * (difficulty / 10);
  // 공인 1RM이 있으면 상대적 강도 배율 적용
  const intensityMultiplier = official1RM && official1RM > 0
    ? Math.min(1.05, Math.max(0.3, weight / official1RM))
    : 1.0;
  return Math.round(baseExp * intensityMultiplier);
}

/**
 * 통합 운동 기록 생성 (게임 피드백 포함)
 */
export interface IntegratedWorkoutInput {
  exerciseId: number;
  sets: number;
  reps: number;
  weight: number;
  workoutDate?: string;
  notes?: string;
}

export interface IntegratedWorkoutResponse {
  workout: {
    id: string;
    exerciseId: number;
    bodyPartId: number;
    sets: number;
    reps: number;
    weight: number;
    calculated1RM: number;
    rmPercentage: number;
    grade: string;
    expGained: number;
    caloriesBurned: number;
    workoutDate: string;
  };
  expGained: number;
  levelUp?: boolean;
  newLevel?: number;
  bodyPartLevelUp?: boolean;
  bodyPartName?: string;
  newBodyPartLevel?: number;
  skills?: {
    newlyUnlocked?: Array<{
      id: number;
      name: string;
      tier: string;
    }>;
  };
  achievements?: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  pointsGained?: number;
  bodyPartProgress?: {
    bodyPartId: number;
    bodyPartName: string;
    oldLevel: number;
    newLevel: number;
    oldExp: number;
    newExp: number;
    expGained: number;
    oldNextLevelExp: number;
    newNextLevelExp: number;
  };
}

export async function createIntegratedWorkout(
  data: IntegratedWorkoutInput
): Promise<IntegratedWorkoutResponse> {
  const response = await axios.post<{ success: boolean; message: string; data: IntegratedWorkoutResponse }>(
    '/workouts/integrated',
    data
  );
  return response.data.data; // 백엔드가 { success, message, data } 형태로 반환
}

/**
 * 최근 운동 기록 조회 (Quick Repeat용)
 */
export interface RecentWorkoutParams {
  bodyPartId?: number;
  limit?: number;
}

export interface RecentWorkout {
  id: number;
  exerciseId: number;
  exerciseName: string;
  bodyPartId: number;
  lastWeight: number;
  lastReps: number;
  lastSets: number;
  lastWorkoutDate: string;
}

export async function getRecentWorkouts(
  params?: RecentWorkoutParams
): Promise<{ data: RecentWorkout[] }> {
  const response = await axios.get<RecentWorkout[]>('/workouts/recent', { params });
  return { data: response.data };
}

/**
 * 부위별 운동 목록 조회
 */
export interface Exercise {
  id: number;
  nameKo: string;
  nameEn: string;
  bodyPartId: number;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  icon?: string;
  description?: string;
}

export async function getExercisesByBodyPart(
  bodyPartId: number
): Promise<{ data: Exercise[] }> {
  const response = await axios.get<Exercise[]>(`/exercises/by-body-part/${bodyPartId}`);
  return { data: response.data };
}

/**
 * 사용자 부위별 레벨 조회
 */
export interface UserBodyPart {
  bodyPartId: number;
  bodyPartName: string;
  level: number;
  currentExp: number;
  requiredExp: number;
  current1RM?: number;
}

export async function getUserBodyParts(): Promise<{ data: UserBodyPart[] }> {
  const response = await axios.get<UserBodyPart[]>('/user/body-parts');
  return { data: response.data };
}

// =====================================
// 이전 기록 및 스마트 추천 API
// =====================================

/**
 * 특정 운동의 이전 기록 조회
 */
export interface PreviousRecordResponse {
  lastRecord: {
    weight: number;
    reps: number;
    sets: number;
    calculated1RM: number;
    grade: string;
    workoutDate: string;
  } | null;
  personalBest: {
    weight: number;
    reps: number;
    calculated1RM: number;
    date: string;
  } | null;
  avgWeight: number;
  avgReps: number;
  totalSessions: number;
}

export async function getPreviousRecord(exerciseId: number): Promise<PreviousRecordResponse> {
  const response = await axios.get<{ success: boolean; data: PreviousRecordResponse }>(
    `/workouts/previous/${exerciseId}`
  );
  return response.data.data;
}

/**
 * 스마트 추천 (Progressive Overload)
 */
export interface SmartRecommendation {
  hasHistory: boolean;
  historyCount?: number;
  averages?: {
    weight: number;
    reps: number;
    sets: number;
  };
  latest?: {
    weight: number;
    reps: number;
    sets: number;
    grade: string;
    date: string;
  };
  recommendation: {
    weight: number;
    reps: number;
    sets: number;
    message: string;
    progressType?: 'WEIGHT_INCREASE' | 'REP_INCREASE' | 'MAINTAIN';
    basedOn?: 'default' | 'bodyweight';
  };
}

export async function getSmartRecommendation(exerciseId: number): Promise<SmartRecommendation> {
  const response = await axios.get<{ success: boolean; data: SmartRecommendation }>(
    `/workouts/recommendation/${exerciseId}`
  );
  return response.data.data;
}

// =====================================
// 스트릭 및 미니 챌린지 API
// =====================================

/**
 * 현재 스트릭 정보 조회
 */
export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  totalWorkoutDays: number;
  lastWorkoutDate: string | null;
  daysSinceLastWorkout?: number;
  streakBonusMultiplier: number;
  thisWeekWorkouts: number;
  streakStatus: 'LEGENDARY' | 'UNSTOPPABLE' | 'ON_FIRE' | 'WARMING_UP' | 'STARTED' | 'COLD';
  nextMilestone: { days: number; reward: string } | null;
}

export async function getStreakInfo(): Promise<StreakInfo> {
  const response = await axios.get<{ success: boolean; data: StreakInfo }>('/streaks');
  return response.data.data;
}

/**
 * 월별 운동 캘린더 조회
 */
export interface CalendarDay {
  count: number;
  totalExp: number;
  bodyParts: number[];
}

export interface CalendarData {
  year: number;
  month: number;
  totalDays: number;
  calendar: Record<string, CalendarDay>;
}

export async function getWorkoutCalendar(year?: number, month?: number): Promise<CalendarData> {
  const response = await axios.get<{ success: boolean; data: CalendarData }>('/streaks/calendar', {
    params: { year, month },
  });
  return response.data.data;
}

/**
 * 미니 챌린지 조회
 */
export interface MiniChallenge {
  id: string;
  title: string;
  description: string;
  bonusExp: number;
  type: 'DAILY' | 'WORKOUT' | 'VARIETY' | 'PR';
}

export interface MiniChallengeData {
  todayWorkouts: number;
  challenges: MiniChallenge[];
  bonusExpAvailable: number;
}

export async function getMiniChallenges(): Promise<MiniChallengeData> {
  const response = await axios.get<{ success: boolean; data: MiniChallengeData }>(
    '/streaks/mini-challenge'
  );
  return response.data.data;
}

/**
 * 미니 챌린지 완료
 */
export interface ChallengeCompleteResponse {
  challengeId: string;
  bonusExpEarned: number;
}

export async function completeMiniChallenge(challengeId: string): Promise<ChallengeCompleteResponse> {
  const response = await axios.post<{ success: boolean; data: ChallengeCompleteResponse }>(
    `/streaks/mini-challenge/${challengeId}/complete`
  );
  return response.data.data;
}

// =====================================
// 운동 루틴 API
// =====================================

/**
 * 루틴 운동 항목
 */
export interface RoutineExercise {
  exerciseId: number;
  exerciseName?: string;
  order: number;
  defaultSets: number;
  defaultReps: number;
  defaultWeight: number | null;
  restSeconds: number;
}

/**
 * 운동 루틴
 */
export interface WorkoutRoutine {
  id: string;
  name: string;
  description?: string;
  exerciseCount: number;
  dayOfWeek: number[];
  isPublic: boolean;
  usageCount: number;
  exercises: RoutineExercise[];
  createdAt: string;
  updatedAt: string;
}

/**
 * 내 루틴 목록 조회
 */
export async function getRoutines(): Promise<WorkoutRoutine[]> {
  const response = await axios.get<{ success: boolean; data: WorkoutRoutine[] }>('/routines');
  return response.data.data;
}

/**
 * 루틴 상세 조회
 */
export interface RoutineDetail extends WorkoutRoutine {
  creatorNickname: string;
  isOwner: boolean;
}

export async function getRoutineById(routineId: string): Promise<RoutineDetail> {
  const response = await axios.get<{ success: boolean; data: RoutineDetail }>(
    `/routines/${routineId}`
  );
  return response.data.data;
}

/**
 * 루틴 생성
 */
export interface CreateRoutineInput {
  name: string;
  description?: string;
  exercises: Array<{
    exerciseId: number;
    order: number;
    defaultSets?: number;
    defaultReps?: number;
    defaultWeight?: number;
    restSeconds?: number;
  }>;
  dayOfWeek?: number[];
  isPublic?: boolean;
}

export async function createRoutine(data: CreateRoutineInput): Promise<{ id: string; name: string }> {
  const response = await axios.post<{
    success: boolean;
    data: { id: string; name: string; exerciseCount: number };
  }>('/routines', data);
  return response.data.data;
}

/**
 * 루틴 수정
 */
export async function updateRoutine(
  routineId: string,
  data: Partial<CreateRoutineInput>
): Promise<{ id: string; name: string }> {
  const response = await axios.patch<{
    success: boolean;
    data: { id: string; name: string; exerciseCount: number };
  }>(`/routines/${routineId}`, data);
  return response.data.data;
}

/**
 * 루틴 삭제
 */
export async function deleteRoutine(routineId: string): Promise<void> {
  await axios.delete(`/routines/${routineId}`);
}

/**
 * 루틴 사용 시작
 */
export interface RoutineUseResponse {
  id: string;
  name: string;
  exercises: Array<{
    exerciseId: number;
    exerciseName: string;
    bodyPartId: number;
    sets: number;
    reps: number;
    weight: number | null;
    restSeconds: number;
  }>;
}

export async function startRoutine(routineId: string): Promise<RoutineUseResponse> {
  const response = await axios.post<{ success: boolean; data: RoutineUseResponse }>(
    `/routines/${routineId}/use`
  );
  return response.data.data;
}

/**
 * 오늘의 추천 루틴
 */
export interface TodaySuggestion {
  hasSuggestion: boolean;
  routine: {
    id: string;
    name: string;
    exerciseCount: number;
    exercises?: Array<{
      exerciseName: string;
      sets: number;
      reps: number;
    }>;
    lastUsed?: string;
  } | null;
  message?: string;
}

export async function getTodaySuggestion(): Promise<TodaySuggestion> {
  const response = await axios.get<{ success: boolean; data: TodaySuggestion }>(
    '/routines/today/suggestion'
  );
  return response.data.data;
}

// =====================================
// 어제 운동 불러오기 API
// =====================================

/**
 * 어제 운동 기록의 개별 운동 항목
 */
export interface YesterdayWorkoutExercise {
  exerciseId: number;
  exerciseName: string;
  bodyPartId: number;
  bodyPartName: string;
  sets: number;
  reps: number;
  weight: number;
}

/**
 * 부위별로 그룹화된 어제 운동
 */
export interface YesterdayWorkoutBodyPart {
  bodyPartId: number;
  bodyPartName: string;
  exercises: YesterdayWorkoutExercise[];
}

/**
 * 어제 운동 응답
 */
export interface YesterdayWorkoutResponse {
  workoutDate: string;
  totalExercises: number;
  bodyParts: YesterdayWorkoutBodyPart[];
  exercises: YesterdayWorkoutExercise[];
}

/**
 * 어제 운동 기록 조회
 * - 어제 운동 기록이 있으면 반환
 * - 없으면 가장 최근 운동일의 기록 반환
 */
export async function getYesterdayWorkouts(): Promise<YesterdayWorkoutResponse | null> {
  const response = await axios.get<{ success: boolean; data: YesterdayWorkoutResponse | null }>(
    '/workouts/yesterday'
  );
  return response.data.data;
}
